import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Database, Cloud, Shield, Zap, Users, Brain, Layers, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { services } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';

const ServicesPage = () => {
  usePageTitle("IT Services", "IT consulting services in UAE — enterprise IT strategy, ERP/CRM advisory, cloud modernization, cybersecurity solutions, enterprise AI and automation, and enterprise architecture by ElitePlus.");
  const iconMap = {
    Target,
    Database,
    Cloud,
    Shield,
    Zap,
    Users,
    Brain,
    Layers
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-6">
            Our Services
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6">
            Our Core <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-xl text-slate-500 max-w-3xl mx-auto leading-relaxed">
            We provide focused consulting services that can scale from assessment to execution.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Target;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  className={`group relative overflow-hidden rounded-3xl ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex flex-col lg:flex lg:items-stretch`}
                >
                  {/* Image Side */}
                  <div className="relative lg:w-1/2 min-h-[300px] lg:min-h-[400px]">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/50 to-transparent" />
                  </div>

                  {/* Content Side */}
                  <div className={`relative lg:w-1/2 p-8 lg:p-12 bg-slate-50 border border-slate-200 ${
                    isEven ? 'lg:rounded-r-3xl lg:border-l-0' : 'lg:rounded-l-3xl lg:border-r-0'
                  } rounded-b-3xl lg:rounded-b-none`}>
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/25">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-800 group-hover:text-cyan-600 transition-colors duration-300">
                          {service.title}
                        </h2>
                        {service.timeline && (
                          <div className="flex items-center gap-2 mt-2 text-slate-500 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>Timeline: {service.timeline}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-slate-600 leading-relaxed mb-6">
                      {service.fullDescription}
                    </p>

                    {/* Deliverables */}
                    <div className="space-y-2 mb-6">
                      <h4 className="text-slate-800 font-semibold text-sm uppercase tracking-wider">What We Deliver</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.deliverables.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-slate-500 text-sm">
                            <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link to={`/services/${service.slug}`}>
                      <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Ready to <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Transform</span>?
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            Let's discuss how we can help your organization achieve its technology goals.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
            >
              Book a Strategy Call
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServicesPage;
